// Shapes of the affiliate-program API (ADR-0042) and the small formatting the
// affiliate portal and the platform console share. Money is integer cents.

export interface LedgerTotals {
  currency: string;
  onHoldCents: number;
  payableCents: number;
  paidCents: number;
  /** Net taken back by adjustments, as a positive number. */
  adjustmentsCents: number;
  /** Not earned: payments of a firm the affiliate belongs to (self-referral). Never paid. */
  excludedCents: number;
}

export interface FunnelCounts {
  clicks: number;
  signups: number;
  payingFirms: number;
}

/**
 * One ledger line as an affiliate sees it: a commission or a generic
 * adjustment, per firm with a partly masked name — never what the firm paid or
 * why money went back.
 */
export interface DashboardEntry {
  id: string;
  kind: 'commission' | 'adjustment';
  /** The firm's masked name, e.g. "A*** Manufacturing". */
  firmLabel: string;
  occurredAt: string;
  currency: string;
  commissionCents: number;
  payableAt: string;
  status: 'on_hold' | 'payable' | 'paid';
}

/** A firm the affiliate referred: masked name, signup date, plan, status and earnings from it. */
export interface ReferredFirm {
  name: string;
  signedUpAt: string;
  /** Null once cancelled. */
  plan: string | null;
  status: 'trial' | 'active' | 'cancelled';
  earnings: Array<{ currency: string; cents: number }>;
}

export interface EarlyPayoutQuote {
  currency: string;
  grossCents: number;
  feeCents: number;
  netCents: number;
}

export interface EarlyPayoutRequest {
  requestedAt: string;
  currency: string;
  grossCents: number;
  feeCents: number;
  netCents: number;
}

export interface DashboardPayout {
  id: string;
  paidAt: string;
  currency: string;
  grossCents: number;
  feeCents: number;
  netCents: number;
}

export interface AffiliateMe {
  name: string;
  email: string;
  referralCode: string;
  referralLink: string;
  funnel: FunnelCounts;
  totals: LedgerTotals[];
  /** A monthly payout happens once the payable balance reaches this. */
  minPayoutCents: number;
  /** What an early payout of each currency's payable balance would pay (absent when nothing would be left). */
  earlyPayoutQuotes: EarlyPayoutQuote[];
  earlyPayoutRequest: EarlyPayoutRequest | null;
  payouts: DashboardPayout[];
  referredFirms: ReferredFirm[];
  entries: DashboardEntry[];
}

export interface PlatformAffiliate {
  id: string;
  name: string;
  email: string;
  referralCode: string;
  status: 'active' | 'suspended';
  createdAt: string;
  funnel: FunnelCounts;
  totals: LedgerTotals[];
}

export interface PayoutReportRow {
  affiliateId: string;
  name: string;
  email: string;
  currency: string;
  /** Gross: what the ledger entries add up to. */
  totalCents: number;
  /** Kept from an early payout; 0 for a regular monthly row. */
  feeCents: number;
  /** What to send: gross less the fee. */
  netCents: number;
  /** An early-payout request (listed whatever the balance) rather than a balance at the minimum. */
  early: boolean;
  entryCount: number;
}

export interface PayoutHistoryRow {
  id: string;
  affiliateId: string;
  affiliateName: string;
  currency: string;
  totalCents: number;
  feeCents: number;
  netCents: number;
  entryCount: number;
  reference: string;
  markedPaidAt: string;
}

export interface PayoutReport {
  asOf: string;
  rows: PayoutReportRow[];
  recentPayouts: PayoutHistoryRow[];
}

/** 123456, 'usd' → "$1,234.56"; a negative amount keeps its sign. */
export function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export const FIRM_STATUS_LABELS: Record<ReferredFirm['status'], string> = {
  trial: 'Trial',
  active: 'Active',
  cancelled: 'Cancelled',
};

/**
 * What affiliates may not do (owner decision 2026-10-24). The same wording is on
 * the marketing site's affiliate FAQ ("What isn't allowed?").
 */
export const PROHIBITED_PROMOTION: readonly string[] = [
  'Don’t send unsolicited email, texts or calls — only message people who expect to hear from you. No spam.',
  'Don’t bid on “ClosingVault” (or close variations or misspellings) in paid search, and don’t run ads that look like they come from us.',
  'Say only what’s true about the product and pricing — no invented discounts, no promised deal outcomes, and don’t claim you work for us.',
  'Don’t refer yourself, your employer, or an account you pay for, and don’t offer cash, gifts or prizes for clicks or sign-ups.',
  'No cookie stuffing, forced or hidden clicks, or auto-loading your link.',
  'Don’t post on coupon or deal sites, or alongside adult, illegal or hateful content.',
];

export const VIOLATION_CONSEQUENCES =
  'If we find a violation, we may cancel commissions on the affected referrals and close your account; for fraud or repeated violations, all unpaid commissions are forfeited. Commission on a refunded or charged-back payment is reversed.';

export const ENTRY_STATUS_LABELS: Record<DashboardEntry['status'], string> = {
  on_hold: 'On hold',
  payable: 'Payable',
  paid: 'Paid',
};

// The affiliate marketing kit: 20 ready-to-post images, published on the public
// marketing site (so the URLs are absolute and the same for DEV and PROD).
export const AFFILIATE_KIT_ZIP_URL =
  'https://closingvault.app/downloads/closingvault-affiliate-kit.zip';
export const AFFILIATE_KIT_CONTENTS = 'Includes LinkedIn post images and profile/company banners.';
export const AFFILIATE_KIT_INSTRUCTION =
  'Post an image with your referral link in the text or first comment.';
/** The four link-preview sized (1200x630) images, one per audience. */
export const AFFILIATE_KIT_PREVIEWS = ['general', 'buyers', 'sellers', 'brokers'].map(
  (audience) => ({
    audience,
    label: audience.charAt(0).toUpperCase() + audience.slice(1),
    url: `https://closingvault.app/media/affiliate-kit/closingvault-affiliate-${audience}-1200x630.png`,
  }),
);

// The five affiliate-only social videos. Members-only: each link is a same-origin
// navigation to the API, so the browser sends the affiliate session cookie (scoped to
// /api/v1/affiliates) and the API answers 401 without it. Never a public URL.
export const AFFILIATE_VIDEOS_INSTRUCTION =
  'Post the video natively and put your referral link in the text or first comment.';
export const AFFILIATE_VIDEOS = [
  { name: 'intro', label: 'Introducing ClosingVault' },
  { name: 'buyers', label: 'For buyers' },
  { name: 'sellers', label: 'For sellers' },
  { name: 'brokers', label: 'For brokers' },
  { name: 'social', label: 'Short demo' },
].map((v) => ({ ...v, url: `/api/v1/affiliates/me/videos/${v.name}` }));
